import { useState, useEffect, useCallback, createContext, useContext } from 'react'

interface ToastMessage {
  id: number
  text: string
  type: 'warning' | 'error' | 'success'
}

interface ToastContextType {
  showToast: (text: string, type?: 'warning' | 'error' | 'success') => void
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} })

export function useToast() {
  return useContext(ToastContext)
}

let nextId = 0

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const showToast = useCallback((text: string, type: 'warning' | 'error' | 'success' = 'warning') => {
    const id = nextId++
    setToasts(prev => [...prev, { id, text, type }])
  }, [])

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 8000)
    return () => clearTimeout(timer)
  }, [toast.id, onDismiss])

  const colors = {
    warning: 'bg-amber-50 border-amber-300 text-amber-800',
    error: 'bg-red-50 border-red-300 text-red-800',
    success: 'bg-green-50 border-green-300 text-green-800',
  }

  return (
    <div className={`${colors[toast.type]} border rounded-lg px-4 py-3 shadow-lg text-sm flex items-start gap-2 animate-slide-in`}>
      <span className="flex-1">{toast.text}</span>
      <button onClick={() => onDismiss(toast.id)} className="opacity-50 hover:opacity-100 text-lg leading-none">&times;</button>
    </div>
  )
}
